import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  LinearProgress,
  Paper,
  IconButton,
  Card,
  CardContent,
  Divider,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { uploadFile } from "../../services/media";

interface FileUploaderProps {
  open: boolean;
  onClose: () => void;
  currentFolderId?: number | null;
  onUploadComplete?: () => void;
}

interface FileUploadState {
  file: File;
  id?: number;
  name: string;
  description: string;
  altText?: string;
  caption?: string;
  author?: string;
  progress: number;
  status: "pending" | "uploading" | "success" | "error";
  error?: string;
  url?: string; // Store the object URL to prevent recreating it on each render
}

const FileUploader: React.FC<FileUploaderProps> = ({
  open,
  onClose,
  currentFolderId,
  onUploadComplete,
}) => {
  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) return;

    const fileList = Array.from(event.target.files);
    const newFiles = fileList.map((file) => {
      // Extract filename without extension to use as initial name
      const nameParts = file.name.split(".");
      const nameWithoutExtension =
        nameParts.length > 1 ? nameParts.slice(0, -1).join(".") : file.name;

      return {
        file,
        name: nameWithoutExtension,
        description: "",
        altText: "",
        caption: "",
        author: "",
        progress: 0,
        status: "pending" as FileUploadState["status"],
        url: URL.createObjectURL(file), // Create URL once and store it
      };
    });

    setFiles((prevFiles) => [...prevFiles, ...newFiles]);

    // Clear the input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prevFiles) => {
      const fileToRemove = prevFiles[index];
      // Revoke the object URL to prevent memory leaks
      if (fileToRemove.url) {
        URL.revokeObjectURL(fileToRemove.url);
      }
      return prevFiles.filter((_, i) => i !== index);
    });
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadComplete(false);

    // Upload files one by one
    for (let i = 0; i < files.length; i++) {
      const fileState = files[i];

      if (fileState.status === "success") continue;

      // Update status to uploading
      setFiles((prevFiles) =>
        prevFiles.map((f, idx) =>
          idx === i ? { ...f, status: "uploading", progress: 0 } : f,
        ),
      );

      try {
        // Simulate progress
        const progressInterval = setInterval(() => {
          setFiles((prevFiles) =>
            prevFiles.map((f, idx) =>
              idx === i && f.progress < 90
                ? { ...f, progress: f.progress + 10 }
                : f,
            ),
          );
        }, 200);

        // Prepare metadata for the file
        const metadata = {
          name: fileState.name,
          description: fileState.description,
          default_alt_text: fileState.altText,
          default_caption: fileState.caption,
          author: fileState.author,
        };

        // Perform actual upload with metadata
        const response = await uploadFile(
          fileState.file,
          currentFolderId,
          metadata,
        );

        clearInterval(progressInterval);

        if (response.success && response.file) {
          setFiles((prevFiles) =>
            prevFiles.map((f, idx) =>
              idx === i
                ? {
                    ...f,
                    status: "success",
                    progress: 100,
                    id: response.file!.id,
                  }
                : f,
            ),
          );
        } else {
          setFiles((prevFiles) =>
            prevFiles.map((f, idx) =>
              idx === i
                ? {
                    ...f,
                    status: "error",
                    progress: 0,
                    error: response.message || "Upload failed",
                  }
                : f,
            ),
          );
        }
      } catch (error) {
        setFiles((prevFiles) =>
          prevFiles.map((f, idx) =>
            idx === i
              ? {
                  ...f,
                  status: "error",
                  progress: 0,
                  error: "Upload failed due to a network error",
                }
              : f,
          ),
        );
      }
    }

    setUploading(false);
    setUploadComplete(true);

    if (onUploadComplete) {
      onUploadComplete();
    }
  };

  const handleCloseDialog = () => {
    if (!uploading) {
      // Revoke all object URLs to prevent memory leaks
      files.forEach((fileState) => {
        if (fileState.url) {
          URL.revokeObjectURL(fileState.url);
        }
      });
      onClose();
      setFiles([]);
      setUploadComplete(false);
    }
  };

  const dropzoneRef = useRef<HTMLDivElement>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const newFiles = droppedFiles.map((file) => {
        // Extract filename without extension to use as initial name
        const nameParts = file.name.split(".");
        const nameWithoutExtension =
          nameParts.length > 1 ? nameParts.slice(0, -1).join(".") : file.name;

        return {
          file,
          name: nameWithoutExtension,
          description: "",
          altText: "",
          caption: "",
          author: "",
          progress: 0,
          status: "pending" as const,
          url: URL.createObjectURL(file), // Create URL once and store it
        };
      });

      setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }
  };

  const renderUploadStatus = () => {
    const totalFiles = files.length;
    const successFiles = files.filter((f) => f.status === "success").length;
    const errorFiles = files.filter((f) => f.status === "error").length;
    const pendingFiles = totalFiles - successFiles - errorFiles;

    return (
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="body2">
          {successFiles} of {totalFiles} files uploaded successfully
          {errorFiles > 0 && `, ${errorFiles} failed`}
          {pendingFiles > 0 && `, ${pendingFiles} pending`}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
      <DialogTitle>
        Upload Files
        <IconButton
          aria-label="close"
          onClick={handleCloseDialog}
          sx={{ position: "absolute", right: 8, top: 8 }}
          disabled={uploading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Dropzone area */}
        <Box
          ref={dropzoneRef}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: "2px dashed",
            borderColor: dragActive ? "primary.main" : "divider",
            borderRadius: 1,
            p: 3,
            textAlign: "center",
            bgcolor: dragActive ? "action.hover" : "background.default",
            mb: 3,
            transition: "all 0.2s",
          }}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            multiple
            hidden
            accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/zip"
          />
          <UploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Drag & Drop files here
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            or
          </Typography>
          <Button
            variant="outlined"
            component="label"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Browse Files
          </Button>
          <Typography
            variant="caption"
            display="block"
            color="textSecondary"
            sx={{ mt: 1 }}
          >
            Supported file types: Images, PDF, Word, Text, ZIP
          </Typography>
        </Box>

        {/* File list */}
        {files.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Files to upload ({files.length})
            </Typography>
            <Grid container spacing={2}>
              {files.map((file, index) => {
                const isImage = file.file.type.startsWith("image/");

                return (
                  <Grid item xs={12} key={index}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor:
                          file.status === "success"
                            ? "success.light"
                            : file.status === "error"
                              ? "error.light"
                              : "background.paper",
                        opacity: file.status === "success" ? 0.8 : 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          mb: file.status !== "pending" ? 2 : 0,
                        }}
                      >
                        {/* Preview */}
                        {isImage && (
                          <Box
                            sx={{
                              mr: 2,
                              width: 60,
                              height: 60,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              component="img"
                              src={file.url} // Use the stored URL instead of creating a new one
                              sx={{
                                maxWidth: 60,
                                maxHeight: 60,
                                objectFit: "contain",
                              }}
                              alt=""
                            />
                          </Box>
                        )}

                        {/* Basic info */}
                        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              variant="body1"
                              noWrap
                              title={file.file.name}
                            >
                              Original: {file.file.name}
                            </Typography>

                            {file.status !== "uploading" && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFile(index)}
                                disabled={uploading}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>

                          <Typography variant="caption" color="textSecondary">
                            {(file.file.size / 1024).toFixed(1)} KB
                          </Typography>

                          {file.status === "uploading" && (
                            <LinearProgress
                              variant="determinate"
                              value={file.progress}
                              sx={{ mt: 1 }}
                            />
                          )}

                          {file.status === "error" && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mt: 0.5,
                              }}
                            >
                              <ErrorIcon fontSize="small" sx={{ mr: 0.5 }} />
                              {file.error || "Upload failed"}
                            </Typography>
                          )}

                          {file.status === "success" && (
                            <Typography
                              variant="caption"
                              color="success.main"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mt: 0.5,
                              }}
                            >
                              <CheckCircleIcon
                                fontSize="small"
                                sx={{ mr: 0.5 }}
                              />
                              Upload complete
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* File metadata editing form - only show for pending files */}
                      {file.status === "pending" && (
                        <Box sx={{ mt: 2 }}>
                          <Divider sx={{ mb: 2 }} />
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                label="Name"
                                fullWidth
                                size="small"
                                value={file.name}
                                onChange={(e) => {
                                  setFiles((prevFiles) =>
                                    prevFiles.map((f, idx) =>
                                      idx === index
                                        ? { ...f, name: e.target.value }
                                        : f,
                                    ),
                                  );
                                }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                label="Description"
                                fullWidth
                                size="small"
                                multiline
                                rows={2}
                                value={file.description}
                                onChange={(e) => {
                                  setFiles((prevFiles) =>
                                    prevFiles.map((f, idx) =>
                                      idx === index
                                        ? { ...f, description: e.target.value }
                                        : f,
                                    ),
                                  );
                                }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                label="Author"
                                fullWidth
                                size="small"
                                value={file.author || ""}
                                onChange={(e) => {
                                  setFiles((prevFiles) =>
                                    prevFiles.map((f, idx) =>
                                      idx === index
                                        ? { ...f, author: e.target.value }
                                        : f,
                                    ),
                                  );
                                }}
                              />
                            </Grid>

                            {isImage && (
                              <>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    label="Alt Text"
                                    fullWidth
                                    size="small"
                                    value={file.altText || ""}
                                    onChange={(e) => {
                                      setFiles((prevFiles) =>
                                        prevFiles.map((f, idx) =>
                                          idx === index
                                            ? { ...f, altText: e.target.value }
                                            : f,
                                        ),
                                      );
                                    }}
                                    placeholder="Alternative text for accessibility"
                                  />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <TextField
                                    label="Caption"
                                    fullWidth
                                    size="small"
                                    value={file.caption || ""}
                                    onChange={(e) => {
                                      setFiles((prevFiles) =>
                                        prevFiles.map((f, idx) =>
                                          idx === index
                                            ? { ...f, caption: e.target.value }
                                            : f,
                                        ),
                                      );
                                    }}
                                  />
                                </Grid>
                              </>
                            )}
                          </Grid>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {uploadComplete && renderUploadStatus()}
          </Box>
        )}

        {files.length === 0 && (
          <Card
            variant="outlined"
            sx={{ bgcolor: "background.default", mb: 2 }}
          >
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <Typography color="textSecondary">
                No files selected for upload
              </Typography>
            </CardContent>
          </Card>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} disabled={uploading}>
          {uploadComplete ? "Close" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={files.length === 0 || uploading}
          startIcon={uploading ? <LinearProgress value={20} /> : <UploadIcon />}
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileUploader;
